import React, { useEffect, useState } from 'react';
import { store as storeApi } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { toast } from '../hooks/use-toast';
import { ShoppingCart, Plus, Minus, X, Loader2 } from 'lucide-react';
import ClampWithMore from '../components/ClampWithMore';
import AppImage from '../components/AppImage';
import SiteSections from '../components/SiteSections';

export default function OnlineStore() {
  const [products, setProducts] = useState([]);
  const [cart, setCart] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const { setUserData } = useAuth();

  useEffect(() => {
    storeApi.products().then(setProducts).finally(() => setLoading(false));
  }, []);

  const add = (p) => {
    setCart(prev => {
      const found = prev.find(i => i.id === p.id);
      if (found) return prev.map(i => i.id === p.id ? { ...i, qty: i.qty + 1 } : i);
      return [...prev, { ...p, qty: 1 }];
    });
    toast({ title: 'Added to cart', description: p.name });
  };
  const upd = (id, delta) => setCart(prev => prev.flatMap(i => i.id === id ? (i.qty + delta <= 0 ? [] : [{ ...i, qty: i.qty + delta }]) : [i]));
  const total = cart.reduce((s, i) => s + i.price * i.qty, 0);

  const checkout = async () => {
    if (!cart.length) return;
    try {
      const res = await storeApi.order(cart.map(c => ({ id: c.id, qty: c.qty })));
      // refresh me
      const meRes = await (await import('../services/api')).auth.me();
      setUserData(meRes);
      setCart([]);
      setOpen(false);
      toast({ title: 'Order placed!', description: `+${res.points_added} points. Thank you for supporting PBB.` });
    } catch (err) {
      toast({ title: 'Checkout failed', description: err?.response?.data?.detail || 'Try again' });
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Online Market Store</h1>
          <p className="text-gray-400 text-sm">Every purchase earns you 10 points per $1.</p>
        </div>
        <button onClick={() => setOpen(true)} className="relative px-3 py-2 rounded-lg border border-[#2a2a2a] hover:border-green-600 text-sm inline-flex items-center gap-2">
          <ShoppingCart size={16}/> Cart
          {cart.length > 0 && <span className="absolute -top-2 -right-2 w-5 h-5 rounded-full bg-green-600 text-white text-[10px] flex items-center justify-center">{cart.reduce((s,i)=>s+i.qty,0)}</span>}
        </button>
      </div>
      {loading ? (
        <div className="flex items-center gap-2 text-gray-400"><Loader2 className="animate-spin" size={16}/> Loading...</div>
      ) : products.length === 0 ? (
        <div className="pbb-card border-2 border-dashed border-green-500/30 bg-green-500/5 p-10 text-center" data-testid="store-empty-state">
          <ShoppingCart className="mx-auto text-green-400" size={30} />
          <h3 className="mt-4 text-xl font-bold">Online Market Store opening soon</h3>
          <p className="mt-2 text-sm text-gray-300 max-w-md mx-auto">Be one of the first 100 members — register now to get early-buyer perks and 10 pts back on every $1 spent when the store activates.</p>
          <a href="/signup" data-testid="store-empty-cta" className="mt-6 inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-green-600 hover:bg-green-500 text-white text-sm font-semibold">Register Free</a>
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
          {products.map(p => (
            <div key={p.id} className="pbb-card overflow-hidden flex flex-col h-full">
              <AppImage src={p.image} alt={p.name} rounded="none" fallback={<ShoppingCart size={24}/>} />
              <div className="p-3 flex flex-col flex-1">
                <div className="font-medium text-sm pbb-card-title" title={p.name}>{p.name}</div>
                <ClampWithMore text={p.description} lines={3} className="text-xs text-gray-400 mt-1" testId={`store-desc-${p.id}`} />
                <div className="flex items-center justify-between mt-2">
                  <div className="text-green-400 font-semibold">${p.price}</div>
                  <button onClick={() => add(p)} className="px-2.5 py-1.5 rounded-lg bg-green-600 hover:bg-green-700 text-xs text-white">Add</button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {open && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-end sm:items-center justify-center p-4" onClick={() => setOpen(false)}>
          <div className="pbb-card w-full max-w-md p-5" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-3">
              <div className="font-semibold">Your Cart</div>
              <button onClick={() => setOpen(false)} className="text-gray-400"><X size={18}/></button>
            </div>
            {cart.length === 0 ? (
              <div className="text-gray-500 py-8 text-center text-sm">Cart is empty.</div>
            ) : (
              <div className="space-y-3 max-h-80 overflow-y-auto">
                {cart.map(i => (
                  <div key={i.id} className="flex items-center gap-3">
                    <img src={i.image} alt="" className="w-14 h-14 rounded object-cover" />
                    <div className="flex-1">
                      <div className="text-sm font-medium">{i.name}</div>
                      <div className="text-xs text-gray-400">${i.price} x {i.qty}</div>
                    </div>
                    <div className="flex items-center gap-1">
                      <button onClick={()=>upd(i.id,-1)} className="w-7 h-7 rounded border border-[#2a2a2a] flex items-center justify-center"><Minus size={14}/></button>
                      <button onClick={()=>upd(i.id,1)} className="w-7 h-7 rounded border border-[#2a2a2a] flex items-center justify-center"><Plus size={14}/></button>
                    </div>
                  </div>
                ))}
              </div>
            )}
            <div className="flex items-center justify-between mt-4 pt-3 border-t border-[#1f1f1f]">
              <div className="text-sm text-gray-400">Total</div>
              <div className="text-xl font-bold text-green-400">${total.toFixed(2)}</div>
            </div>
            <button onClick={checkout} disabled={!cart.length} className="pbb-btn-green mt-3 disabled:opacity-50 disabled:cursor-not-allowed">Checkout</button>
          </div>
        </div>
      )}
      <SiteSections pageKey="store" />
    </div>
  );
}
